// frontend/src/widgets/DocumentLibraryWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Document & Content Management
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-08-28
// Updated:      2026-09-08 08:22 BST
//
// Purpose:
//   Dashboard-ready document library for browsing and uploading tenant-scoped
//   documents associated with customers and jobs.
//
// Role:
//   Provides read/browse access to documents plus the existing upload workflow.
//   Tenant scope is implicit and resolved by the authenticated backend; this
//   component never supplies or defaults tenant_id.
//
// Lifecycle Status:
//   Active. Retained as the compact document-management widget for dashboard
//   and contextual customer/job surfaces.
//
// Change Summary (2026-09-08):
//   - Removed the unsafe `tenantId = 1` default and all frontend-supplied
//     tenant_id query/form values. Canonical tenant resolution is JWT -> User ->
//     tenant_id on the backend.
//   - Retained customer_id and job_id as legitimate resource filters/context.
//   - Preserved the existing `/documents` list and `/documents/upload` flows.
//   - Reworked the six-column desktop table into labelled mobile document cards
//     using the same table/row DOM and the same loaded rows.
//   - Measures the tallest rendered mobile document card so one complete record
//     is visible in the internal document viewport before scrolling.
//   - Added mobile-safe search/upload controls and natural widget host sizing.
//   - Reworked Upload Document into a near-full-screen mobile sheet with
//     touch-safe inputs, close control, and stacked actions.
//   - Added visible load/upload error states without changing backend contracts.
//
// Operational Notes:
//   - Do not reintroduce tenant_id into query params, props, FormData, or other
//     client-controlled request data.
//   - `customerId` and `jobId` remain optional contextual filters only.
//   - The browser/search list uses the centralized axiosInstance.
//   - Mobile uses one mounted document list; no duplicate mobile/desktop fetch.
//
// Future Intent:
//   Keep this widget as the compact document-library surface. Richer document
//   metadata, permissions, version history, signatures, or records-management
//   actions should remain separate governed capabilities rather than expanding
//   this widget into a second Records Management implementation.

import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext";
import { format } from "date-fns";

const MOBILE_BREAKPOINT = 768;
const MOBILE_CARD_FALLBACK_HEIGHT = 360;

const formatUploadedAt = (value) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return String(value);
  }
};

const formatSize = (value) => {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";

  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(bytes / 1024).toFixed(1)} KB`;
};

const mobileLabelStyle = {
  fontSize: "0.72rem",
  fontWeight: 800,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.035em",
  lineHeight: 1.25,
};

export default function DocumentLibraryWidget({
  customerId = null,
  jobId = null,
}) {
  const { theme } = useTheme();

  const rootRef = useRef(null);
  const listRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false
  );
  const [mobileCardViewportHeight, setMobileCardViewportHeight] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    setError("");

    try {
      const params = { q };
      if (customerId) params.customer_id = customerId;
      if (jobId) params.job_id = jobId;

      const { data } = await axiosInstance.get("/documents", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(
        "Failed to load documents:",
        err?.response?.data || err?.message || err
      );
      setRows([]);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Failed to load documents."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, customerId, jobId]);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    if (!isMobile || loading || rows.length === 0) {
      setMobileCardViewportHeight(null);
      return undefined;
    }

    const list = listRef.current;
    if (!list) return undefined;

    let frameId = null;
    let observer = null;

    const measureCards = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const cards = Array.from(
          list.querySelectorAll('[data-mobile-document-row="true"]')
        );

        if (cards.length === 0) return;

        const tallest = cards.reduce((maxHeight, card) => {
          const height = Math.ceil(card.getBoundingClientRect().height);
          return Math.max(maxHeight, height);
        }, 0);

        if (tallest > 0) {
          setMobileCardViewportHeight(tallest);
        }
      });
    };

    measureCards();
    window.addEventListener("resize", measureCards);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measureCards);
      observer.observe(list);
    }

    return () => {
      window.removeEventListener("resize", measureCards);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isMobile, loading, rows]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [
    isMobile,
    loading,
    error,
    rows.length,
    mobileCardViewportHeight,
    showUpload,
  ]);

  const mobileCellStyle = (isLast = false) =>
    isMobile
      ? {
          display: "grid",
          gridTemplateColumns: "88px minmax(0, 1fr)",
          gap: "10px",
          alignItems: "start",
          padding: "10px 12px",
          borderBottom: isLast ? "none" : "1px solid #efefef",
          minWidth: 0,
          whiteSpace: "normal",
        }
      : undefined;

  const mobileListHeight =
    mobileCardViewportHeight || MOBILE_CARD_FALLBACK_HEIGHT;

  const actionBackground =
    theme?.button_action?.normal || theme?.accent_color || "var(--accent-color, #1e90ff)";

  return (
    <div
      ref={rootRef}
      className="rounded-2xl shadow-sm p-4"
      style={{
        background: "#fff",
        height: isMobile ? "auto" : "100%",
        minHeight: 0,
        overflow: isMobile ? "visible" : undefined,
        boxSizing: "border-box",
      }}
    >
      <div
        className="mb-3"
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isMobile ? 10 : 12,
        }}
      >
        <h3 className="text-lg font-semibold" style={{ margin: 0 }}>
          Documents
        </h3>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 8,
            width: isMobile ? "100%" : "auto",
          }}
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search documents"
            className="border rounded-lg px-3 py-1.5"
            style={{
              minWidth: isMobile ? 0 : 220,
              width: isMobile ? "100%" : undefined,
              minHeight: isMobile ? 44 : undefined,
              fontSize: isMobile ? 16 : undefined,
              boxSizing: "border-box",
            }}
          />

          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="px-3 py-1.5 rounded-xl"
            style={{
              width: isMobile ? "100%" : undefined,
              minHeight: isMobile ? 44 : undefined,
              background: actionBackground,
              color: "#fff",
              fontWeight: 700,
            }}
          >
            Upload
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#991b1b",
            fontSize: 13,
            fontWeight: 700,
            overflowWrap: "anywhere",
          }}
        >
          {error}
        </div>
      )}

      <div
        ref={listRef}
        className={isMobile ? "" : "overflow-auto"}
        style={{
          overflowX: isMobile ? "hidden" : undefined,
          overflowY: isMobile && rows.length > 0 && !loading ? "auto" : undefined,
          height:
            isMobile && rows.length > 0 && !loading
              ? `${mobileListHeight}px`
              : undefined,
          maxHeight:
            isMobile && rows.length > 0 && !loading
              ? `${mobileListHeight}px`
              : undefined,
          minHeight:
            isMobile && rows.length > 0 && !loading
              ? `${mobileListHeight}px`
              : undefined,
          border: isMobile ? "1px solid #ececec" : undefined,
          borderRadius: isMobile ? 10 : undefined,
          background: isMobile ? "#fff" : undefined,
          scrollSnapType: isMobile ? "y mandatory" : undefined,
          overscrollBehavior: isMobile ? "contain" : undefined,
          WebkitOverflowScrolling: isMobile ? "touch" : undefined,
        }}
      >
        <table
          className="min-w-full text-sm"
          style={{
            width: "100%",
            borderCollapse: isMobile ? "separate" : "collapse",
            borderSpacing: 0,
            display: isMobile ? "block" : "table",
          }}
        >
          <thead style={{ display: isMobile ? "none" : "table-header-group" }}>
            <tr className="text-left border-b">
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Size</th>
              <th className="py-2 pr-3">Customer</th>
              <th className="py-2 pr-3">Job</th>
              <th className="py-2 pr-3">Uploaded</th>
            </tr>
          </thead>

          <tbody style={{ display: isMobile ? "block" : "table-row-group" }}>
            {loading ? (
              <tr style={{ display: isMobile ? "block" : "table-row" }}>
                <td
                  colSpan={6}
                  className="py-4"
                  style={{
                    display: isMobile ? "block" : undefined,
                    padding: isMobile ? "16px 12px" : undefined,
                  }}
                >
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr style={{ display: isMobile ? "block" : "table-row" }}>
                <td
                  colSpan={6}
                  className="py-4"
                  style={{
                    display: isMobile ? "block" : undefined,
                    padding: isMobile ? "16px 12px" : undefined,
                  }}
                >
                  No documents found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  data-mobile-document-row="true"
                  className={isMobile ? "" : "border-b last:border-0"}
                  style={{
                    display: isMobile ? "block" : "table-row",
                    width: isMobile ? "100%" : undefined,
                    minWidth: 0,
                    background: "#fff",
                    borderBottom: isMobile ? "1px solid #ececec" : undefined,
                    scrollSnapAlign: isMobile ? "start" : undefined,
                    scrollSnapStop: isMobile ? "always" : undefined,
                  }}
                >
                  <td
                    className={isMobile ? "" : "py-2 pr-3"}
                    style={mobileCellStyle()}
                  >
                    {isMobile && <div style={mobileLabelStyle}>Name</div>}
                    <div
                      style={{
                        minWidth: 0,
                        fontWeight: isMobile ? 700 : undefined,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {row.latest_url ? (
                        <a
                          href={row.latest_url}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                          style={{ overflowWrap: "anywhere" }}
                        >
                          {row.name}
                        </a>
                      ) : (
                        row.name
                      )}
                    </div>
                  </td>

                  <td
                    className={isMobile ? "" : "py-2 pr-3"}
                    style={mobileCellStyle()}
                  >
                    {isMobile && <div style={mobileLabelStyle}>Type</div>}
                    <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      {row.mime_type || "—"}
                    </div>
                  </td>

                  <td
                    className={isMobile ? "" : "py-2 pr-3"}
                    style={mobileCellStyle()}
                  >
                    {isMobile && <div style={mobileLabelStyle}>Size</div>}
                    <div>{formatSize(row.size_bytes)}</div>
                  </td>

                  <td
                    className={isMobile ? "" : "py-2 pr-3"}
                    style={mobileCellStyle()}
                  >
                    {isMobile && <div style={mobileLabelStyle}>Customer</div>}
                    <div>{row.customer_id ?? "—"}</div>
                  </td>

                  <td
                    className={isMobile ? "" : "py-2 pr-3"}
                    style={mobileCellStyle()}
                  >
                    {isMobile && <div style={mobileLabelStyle}>Job</div>}
                    <div>{row.job_id ?? "—"}</div>
                  </td>

                  <td
                    className={isMobile ? "" : "py-2 pr-3"}
                    style={mobileCellStyle(true)}
                  >
                    {isMobile && <div style={mobileLabelStyle}>Uploaded</div>}
                    <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      {formatUploadedAt(row.created_at)}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onDone={() => {
            setShowUpload(false);
            fetchList();
          }}
          defaultCustomerId={customerId}
          defaultJobId={jobId}
          isMobile={isMobile}
          theme={theme}
        />
      )}
    </div>
  );
}

function UploadModal({
  onClose,
  onDone,
  defaultCustomerId,
  defaultJobId,
  isMobile,
  theme,
}) {
  const [file, setFile] = useState(null);
  const [tags, setTags] = useState("");
  const [customerId, setCustomerId] = useState(defaultCustomerId || "");
  const [jobId, setJobId] = useState(defaultJobId || "");
  const [customerRef, setCustomerRef] = useState("");
  const [jobRef, setJobRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleUpload = async () => {
    if (!file || !customerRef || busy) return;

    setBusy(true);
    setUploadError("");

    try {
      const form = new FormData();

      if (customerId) form.append("customer_id", String(customerId));
      if (jobId) form.append("job_id", String(jobId));
      form.append("customer_ref", customerRef);
      if (jobRef) form.append("job_ref", jobRef);
      if (tags) form.append("tags", tags);
      form.append("file", file);

      await axiosInstance.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      onDone();
    } catch (err) {
      console.error(
        "Document upload failed:",
        err?.response?.data || err?.message || err
      );
      setUploadError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Document upload failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle = {
    width: "100%",
    minHeight: isMobile ? 44 : undefined,
    boxSizing: "border-box",
    fontSize: isMobile ? 16 : undefined,
  };

  const actionBackground =
    theme?.button_action?.normal ||
    theme?.accent_color ||
    "var(--accent-color, #1e90ff)";

  return (
    <div
      className="fixed inset-0 bg-black/40 flex z-50"
      style={{
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: isMobile ? "stretch" : "center",
        padding: isMobile ? 8 : 24,
      }}
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="bg-white shadow-xl w-full"
        style={{
          maxWidth: isMobile ? "none" : 512,
          maxHeight: isMobile ? "calc(100dvh - 16px)" : "90vh",
          overflowY: "auto",
          borderRadius: isMobile ? 14 : 16,
          padding: isMobile ? 14 : 24,
          margin: isMobile ? 0 : undefined,
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="mb-4"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <h4 className="text-lg font-semibold" style={{ margin: 0 }}>
            Upload Document
          </h4>

          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full bg-gray-100"
            style={{
              width: 44,
              height: 44,
              minWidth: 44,
              border: "none",
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.6 : 1,
              fontSize: 18,
              fontWeight: 700,
            }}
            aria-label="Close upload dialog"
          >
            ✕
          </button>
        </div>

        <div className="grid gap-3">
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="border rounded-lg px-3 py-2"
            style={fieldStyle}
          />

          <input
            value={customerRef}
            onChange={(e) => setCustomerRef(e.target.value)}
            placeholder="Customer Ref (e.g. CUST-0001) *"
            className="border rounded-lg px-3 py-2"
            style={fieldStyle}
          />

          <input
            value={jobRef}
            onChange={(e) => setJobRef(e.target.value)}
            placeholder="Job Ref (optional)"
            className="border rounded-lg px-3 py-2"
            style={fieldStyle}
          />

          <input
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            placeholder="Customer ID (optional)"
            inputMode="numeric"
            className="border rounded-lg px-3 py-2"
            style={fieldStyle}
          />

          <input
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            placeholder="Job ID (optional)"
            inputMode="numeric"
            className="border rounded-lg px-3 py-2"
            style={fieldStyle}
          />

          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Tags (comma-separated)"
            className="border rounded-lg px-3 py-2"
            style={fieldStyle}
          />

          {uploadError && (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #fecaca",
                background: "#fef2f2",
                color: "#991b1b",
                fontSize: 13,
                fontWeight: 700,
                overflowWrap: "anywhere",
              }}
            >
              {uploadError}
            </div>
          )}

          <div
            className="mt-2"
            style={{
              display: "flex",
              flexDirection: isMobile ? "column-reverse" : "row",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-1.5 rounded-xl bg-gray-100"
              style={{
                width: isMobile ? "100%" : undefined,
                minHeight: isMobile ? 44 : undefined,
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.6 : 1,
                fontWeight: 700,
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleUpload}
              disabled={busy || !file || !customerRef}
              className="px-3 py-1.5 rounded-xl text-white"
              style={{
                width: isMobile ? "100%" : undefined,
                minHeight: isMobile ? 44 : undefined,
                background: actionBackground,
                opacity: busy || !file || !customerRef ? 0.6 : 1,
                cursor:
                  busy || !file || !customerRef ? "not-allowed" : "pointer",
                fontWeight: 700,
              }}
            >
              {busy ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
